/**
 * Market making (docs/market-making-ux.md) — quote both sides of a market across venues, watch
 * the fills and the exposure, and get out. greenbook never trades on its own: every order here,
 * hedges included, is placed by the user pressing a button.
 *
 *   header    "Market making", Real · USD / Paper · ATLAS (follows the global mode, switches
 *             freely), "?" (replays the walkthrough), [+ New quote]
 *   cards     one per Market making quote, exposed first, then newest. The list is the server's:
 *             every opportunity filed under the "greenbook · Market making" model (lib/mmModel.ts),
 *             on any device, merged with this browser's registry (a local cache of what was placed
 *             here, and the legacy quotes from before models, which only it knows; `mergeQuotes`).
 *             A quote placed on another device is rebuilt from its legs and the game's books
 *             (`entryFromOpportunity`). Remove hides a card in this browser. Revision 2:
 *             one big live number (MmKpi) whose colour says how settled it is (Working → Exposed
 *             "Get out now" → Locked → Settled), the primary action beside it (Get out / Move bid),
 *             a fill line per side in contracts, and everything else behind Details
 *   builder   components/marketMaking/QuoteBuilder.tsx
 *   get out   components/marketMaking/GetOut.tsx (Get out + Move bid confirms); a custom-amount
 *             get-out (HedgePanel.tsx) lives under Details. These single orders go through
 *             order-sheet sheets (useMmSheet.ts) with the opportunity id and its model; Place quote
 *             stays one headless multi-leg `om.buy`, filed under the model (created lazily, on the
 *             first quote placed, never on page load)
 *   tour      components/marketMaking/Walkthrough.tsx: the 3-panel explainer (empty page / first
 *             visit), coach marks in the first quote, a tip on the card after placing
 *
 * Reads everything through a `MarketMakingSource` so the dev preview (/dev/market-making) renders
 * the same screen from mocks; `liveSource` below wires the real hooks and the order SDK.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import type { LiquidityPosition, Opportunity } from "@greenbook/client";
import type { MoneyMode } from "@openmarketsai/order-sheet";
import {
  invalidateAfterOrder,
  useAccountPartners,
  useContest,
  useContestMarkets,
  useContests,
  useLeagues,
  useLiquidity,
  usePartners,
} from "../lib/queries.js";
import { ApiError, apiGet } from "../lib/api.js";
import { ensureMmModel, useMmModelQuery, useMmServerQuotes } from "../lib/mmModel.js";
import { queryClient } from "../lib/queryClient.js";
import { authIdentity } from "../lib/omauth.js";
import { useOrderClient } from "../lib/orderClient.js";
import { useLiveContest } from "../lib/ws.js";
import { SCOPES, useCanTrade, useIsAuthed } from "../lib/omauth.js";
import { setTradingMode, useTradingMode } from "../lib/trading.js";
import { showUnlock } from "../components/Gate.js";
import { useOddsFormat } from "../lib/odds.js";
import { fmtDateTime, fmtPrice } from "../lib/format.js";
import { findComplement, fillPct, type Ccy } from "../lib/portfolio.js";
import { Link, useSearchParams } from "react-router-dom";
import { marketHref } from "../lib/marketView.js";
import {
  addRegistryLegs,
  bookParticipantId,
  bidsVsMarket,
  buildOppBook,
  MOVED_AWAY_CENTS,
  shortGame,
  shortMarket,
  shortSide,
  type BidVsMarket,
  classifyStatus,
  defaultStorage,
  entryFromOpportunity,
  forgetOpportunity,
  hideOpportunity,
  legVenueName,
  mergeQuotes,
  quotableMarkets,
  getOut,
  hedgeNeed,
  ifAllFill,
  isResting,
  KPI_LABEL,
  kpiState,
  liveQuotes,
  longRestingIds,
  movedAway,
  mmFocusHref,
  registerOpportunity,
  requoteFrom,
  settledResult,
  sortLegs,
  sortOpportunities,
  useMmHidden,
  useMmRegistry,
  readTourSeen,
  useMarketMakingAccess,
  writeTourSeen,
  type MmEntry,
  type MmLeg,
  type MmStatus,
  type OppBook,
  type PlacedLeg,
  type SideBook,
} from "../lib/marketMaking.js";
import { BottomSheet, FlashText, Pill, SectionHeading, Skeleton, SoftCard } from "../components/kit.js";
import { useIsMobile } from "../lib/responsive.js";
import { useStoreHoldings, useStoreProducts, usePurchase } from "../lib/store.js";
import { UnlockQuotes } from "../components/marketMaking/Unlock.js";
import { canAddQuote, offersFor, tierOf, TIER_LIMIT, TIER_NAME } from "../lib/mmPlan.js";
import { ErrorState } from "../components/ui.js";
import { QuoteBuilder, type QuotePrefill, type QuoteStart } from "../components/marketMaking/QuoteBuilder.js";
import { HedgePanel } from "../components/marketMaking/HedgePanel.js";
import { GetOutConfirm, MoveBidConfirm } from "../components/marketMaking/GetOut.js";
import { KpiChip, MmKpi } from "../components/marketMaking/MmKpi.js";
import { COACH_STEPS, Explainer, PostPlaceTip, type Coach, type CoachStep } from "../components/marketMaking/Walkthrough.js";
import {
  btnBase,
  btnDanger,
  btnIdle,
  btnOn,
  btnPrimary,
  ccyOf,
  errorText,
  fmtBid,
  fmtContracts,
  gameLabel,
  modeOf,
  money,
  PlacedResult,
  signedMoney,
  toneOf,
  type MarketMakingSource,
  type OppState,
  type ServerQuotes,
} from "../components/marketMaking/shared.js";

/* ============================================================== live source */

const POLL_RESTING_MS = 10_000;

const hasResting = (o: Opportunity | undefined): boolean => (o?.legs ?? []).some((l) => isResting(l as MmLeg));
const NO_OPPS: Opportunity[] = [];

export const liveSource: MarketMakingSource = {
  useRegistry() {
    return useMmRegistry();
  },
  register(entry) {
    registerOpportunity(entry);
  },
  addLegs(id, legs) {
    addRegistryLegs(id, legs);
  },
  forget(id) {
    // A server-listed quote stays on the account and in the model's list, so Remove also hides
    // it in this browser (a local set); a local entry is forgotten too.
    forgetOpportunity(id);
    hideOpportunity(id);
  },
  useHidden() {
    return useMmHidden();
  },
  useServerQuotes() {
    // Reads only: GET /auth/models (finds the model) and its opportunities. Never creates it.
    const model = useMmModelQuery();
    const list = useMmServerQuotes(model.data);
    return useMemo((): ServerQuotes => {
      const noModel = model.data === null;
      // Known once it answered, failed, or isn't being asked (signed out / no account:read).
      const modelKnown = model.data !== undefined || model.isError || model.fetchStatus === "idle";
      return {
        items: noModel ? NO_OPPS : list.data,
        settled: model.data ? list.data !== undefined || list.isError : modelKnown,
        isError: model.isError || list.isError,
        error: model.error ?? list.error,
        refetch: () => {
          void model.refetch();
          if (model.data) void list.refetch();
        },
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model.data, model.isError, model.fetchStatus, model.error, list.data, list.isError, list.error]);
  },
  ensureModel() {
    return ensureMmModel(queryClient, authIdentity());
  },
  useOpportunities(ids) {
    // Under ["opportunities", …] so the orders stream's invalidation (ws.ts) refreshes them too.
    const results = useQueries({
      queries: ids.map((id) => ({
        queryKey: ["opportunities", "one", id],
        queryFn: async ({ signal }: { signal: AbortSignal }) => (await apiGet<Opportunity>(`/opportunities/${encodeURIComponent(id)}`, signal)).data,
        staleTime: 5_000,
        refetchInterval: (q: { state: { data?: Opportunity } }) => (hasResting(q.state.data) ? POLL_RESTING_MS : false),
        retry: (n: number, e: unknown) => !(e instanceof ApiError && e.status === 404) && n < 2,
      })),
    });
    const sig = ids.map((id, i) => `${id}:${results[i]?.dataUpdatedAt ?? 0}:${results[i]?.status}`).join(",");
    const map = useMemo(() => {
      const m = new Map<string, OppState>();
      ids.forEach((id, i) => {
        const r = results[i];
        if (!r) return;
        m.set(id, {
          data: r.data,
          isLoading: r.isLoading,
          isError: r.isError,
          notFound: r.error instanceof ApiError && r.error.status === 404,
          error: r.error,
          refetch: () => void r.refetch(),
        });
      });
      return m;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sig]);
    return useCallback(
      (id: string) =>
        map.get(id) ?? {
          data: undefined,
          isLoading: true,
          isError: false,
          notFound: false,
          error: null,
          refetch: () => {},
        },
      [map],
    );
  },
  useSidePrices(contestId) {
    // Stream this game's prices (patched into every ["liquidity", id, …] cache by the bridge).
    // One shared full-liquidity query per game: the cards and the quote builder (useGameBook) read
    // the same cache, so a busy game's ~3 MB download happens once, not once per consumer.
    useLiveContest(contestId);
    return useLiquidity(contestId).data;
  },
  useScope(mode) {
    const partnersQ = useAccountPartners();
    const catalog = usePartners();
    return useMemo(() => {
      if (mode !== "real") return { allowed: null, limitVenues: null, loading: false };
      const allowed = new Set<string>();
      for (const ap of partnersQ.data?.account_partners ?? []) {
        if (ap.status === "active" && ap.credential_status === "connected") allowed.add(ap.partner_id);
      }
      // A venue takes limits unless its order_config says otherwise (unknown → allowed; the API refuses).
      const limitVenues = catalog.data
        ? new Set(
            catalog.data
              .filter((p) => {
                const t = p.order_config?.supported_order_types;
                return !Array.isArray(t) || t.includes("limit");
              })
              .map((p) => p.partner_id),
          )
        : null;
      return { allowed, limitVenues, loading: partnersQ.isLoading };
    }, [mode, partnersQ.data, partnersQ.isLoading, catalog.data]);
  },
  useGames(q) {
    const r = useContests({
      status: "open",
      q: q.length >= 2 ? q.slice(0, 100) : undefined,
      limit: 200,
    });
    const items = useMemo(() => (r.data?.pages ?? []).flatMap((p) => p.data), [r.data]);
    return {
      items,
      isLoading: r.isLoading,
      isError: r.isError,
      error: r.error,
      refetch: () => void r.refetch(),
    };
  },
  useContest(id) {
    return useContest(id).data;
  },
  useLeagues() {
    const q = useLeagues();
    const map = useMemo(() => new Map((q.data ?? []).map((l) => [l.id, l])), [q.data]);
    return useCallback((id: string | undefined) => (id ? map.get(id) : undefined), [map]);
  },
  useGameBook(contestId) {
    useLiveContest(contestId);
    const liq = useLiquidity(contestId);
    const mk = useContestMarkets(contestId);
    return {
      positions: liq.data,
      markets: mk.data ?? (mk.isError ? [] : undefined),
      isLoading: liq.isLoading || mk.isLoading,
      isError: liq.isError,
      error: liq.error,
      refetch: () => {
        void liq.refetch();
        void mk.refetch();
      },
    };
  },
  useOrders() {
    const om = useOrderClient();
    const authed = useIsAuthed();
    const canTradeReal = useCanTrade();
    const qc = useQueryClient();
    return useMemo(
      () => ({
        client: (om ? "ok" : authed ? "no_session" : "signed_out") as "ok" | "no_session" | "signed_out",
        canTradeReal,
        unlockReal: () => showUnlock(SCOPES.trade),
        om,
        afterOrder: () => invalidateAfterOrder(qc),
        buy: async (legs, opts) => {
          if (!om) throw new Error("Sign in with OpenMarkets to trade.");
          try {
            return await om.buy(legs, { ...(opts?.opportunityId ? { opportunityId: opts.opportunityId } : {}), ...(opts?.modelId ? { modelId: opts.modelId } : {}) });
          } finally {
            invalidateAfterOrder(qc);
          }
        },
        cancel: async (ids) => {
          if (!om) throw new Error("Sign in with OpenMarkets to trade.");
          try {
            return await om.cancelOrders(ids);
          } finally {
            invalidateAfterOrder(qc);
          }
        },
      }),
      [om, authed, canTradeReal, qc],
    );
  },
  useMode() {
    const want = useTradingMode();
    // The global switch decides the legs' mode. Real is only ARMED with trade:execute too (the
    // order client refuses a real leg otherwise, see orderClient.ts); Real without the scope is
    // never quietly downgraded to paper — the builder and the hedge block and offer Unlock.
    return { mode: want, wantsReal: want === "real", setMode: setTradingMode };
  },
  usePlan() {
    const holdings = useStoreHoldings();
    const has = (code: string) => (holdings.data?.entitlements ?? []).some((e) => e.code === code);
    const tier = tierOf(has);
    // The product list only matters when there's something left to buy.
    const products = useStoreProducts(tier !== "unlimited");
    const purchase = usePurchase();
    const err = holdings.error ?? products.error;
    return {
      tier,
      loading: holdings.isLoading,
      error: err ? errorText(err) : null,
      offers: offersFor(tier, products.data?.products ?? null),
      purchase,
    };
  },
  readTourSeen() {
    return readTourSeen(defaultStorage());
  },
  writeTourSeen(seen) {
    writeTourSeen(seen, defaultStorage());
  },
};

/**
 * `/market-making?quote=1&c=<contestId>&m=<marketKey>&b=<bookKey>` opens a new quote on that market
 * and line (the Market screen's [Make market]); it goes through the plan gate like "+ New quote".
 * `?focus=<opportunityId>` scrolls to that card (the Market screen's "Open card").
 */
export function mmQuoteHref(t: { contestId: string; marketKey: string; bookKey: string }): string {
  return `/market-making?${new URLSearchParams({ quote: "1", c: t.contestId, m: t.marketKey, b: t.bookKey }).toString()}`;
}
export { mmFocusHref };

/** The routed page (`/market-making`). */
export function MarketMaking() {
  const allowed = useMarketMakingAccess();
  const [params, setParams] = useSearchParams();
  const c = params.get("c");
  const m = params.get("m");
  const b = params.get("b");
  const openQuote = useMemo<OpenQuote | undefined>(
    () => (params.get("quote") === "1" && c ? { contestId: c, start: m ? { marketKey: m, participantId: bookParticipantId(b), bookKey: b } : {} } : undefined),
    [params, c, m, b],
  );
  const focus = params.get("focus") ?? undefined;
  const consumed = useCallback(() => setParams({}, { replace: true }), [setParams]);
  if (!allowed) {
    // The planned one-time purchase lands here.
    return (
      <div className="p-4">
        <SoftCard className="p-4 text-[13px] text-dim">Market making isn't unlocked on this account.</SoftCard>
      </div>
    );
  }
  return <MarketMakingScreen source={liveSource} openQuote={openQuote} focus={focus} onLinkUsed={consumed} />;
}

/** A request to open the builder on a market (from a link), checked against the plan first. */
export interface OpenQuote {
  contestId: string;
  start: QuoteStart;
}

/* =================================================================== screen */

interface BuilderView {
  kind: "builder";
  prefill?: QuotePrefill;
  /** Open on this game. */
  contestId?: string | null;
  /** Paper for this quote only (the walkthrough's "Try it with paper"). */
  paper?: boolean;
  /** Start on this margin (%) instead of 2% (dev preview). */
  margin?: number;
  /** Open further along the steps: this market / participant / line (dev preview). */
  start?: QuoteStart;
}
type View = { kind: "list" } | BuilderView | { kind: "unlock" };

/** Dev preview: open straight on a builder, or on a re-quote of one of the opportunities. */
export type InitialView = (Omit<BuilderView, "kind" | "prefill"> & { kind: "builder" }) | { kind: "requote"; opportunityId: string };

export function MarketMakingScreen({
  source,
  initialCcy,
  initialView,
  openQuote,
  focus,
  onLinkUsed,
}: {
  source: MarketMakingSource;
  /** Dev preview: start on this book instead of following the trading mode. */
  initialCcy?: Ccy;
  /** Dev preview: open the quote builder (or a re-quote) straight away. */
  initialView?: InitialView;
  /** A link asked for a new quote on this market (opens once the plan is known; Free may get Unlock). */
  openQuote?: OpenQuote;
  /** A link asked for this opportunity's card. */
  focus?: string;
  /** The link's request was handled (the route clears its query). */
  onLinkUsed?: () => void;
}) {
  const { mode, wantsReal } = source.useMode();
  const globalCcy: Ccy = wantsReal ? "USD" : "ATLAS";
  const [ccy, setCcy] = useState<Ccy>(initialCcy ?? globalCcy);
  // Follows the global trading mode; the pills switch freely.
  useEffect(() => {
    if (initialCcy == null) setCcy(globalCcy);
  }, [globalCcy, initialCcy]);

  // ── the walkthrough: explainer on a first visit, coach marks in that first quote, a tip after ──
  const [seen, setSeen] = useState(() => source.readTourSeen());
  const [explainer, setExplainer] = useState(!seen);
  const [coachStep, setCoachStep] = useState<CoachStep | null>(null);
  const [postTip, setPostTip] = useState(false);
  const touring = useRef(false);
  const markSeen = useCallback(() => {
    setSeen(true);
    source.writeTourSeen(true);
  }, [source]);
  const coach: Coach = useMemo(
    () => ({
      step: coachStep,
      next: () => {
        const i = coachStep ? COACH_STEPS.indexOf(coachStep) : -1;
        const nxt = COACH_STEPS[i + 1] ?? null;
        setCoachStep(nxt);
        if (!nxt) markSeen();
      },
      skip: () => {
        setCoachStep(null);
        touring.current = false;
        markSeen();
      },
    }),
    [coachStep, markSeen],
  );
  const replay = () => {
    setSeen(false);
    source.writeTourSeen(false);
    setExplainer(true);
    setPostTip(false);
  };

  const initBuilder = initialView?.kind === "builder" ? initialView : null;
  const [view, setView] = useState<View>(initBuilder ? { ...initBuilder } : { kind: "list" });
  const [focusId, setFocusId] = useState<string | null>(focus ?? null);
  // A linked card: shown in its own book, scrolled to once it has rendered.
  const linkScroll = useRef(false);
  useEffect(() => {
    if (!focus) return;
    setFocusId(focus);
    linkScroll.current = true;
    onLinkUsed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);
  const [lastPlaced, setLastPlaced] = useState<{
    id: string;
    legs: PlacedLeg[];
  } | null>(null);

  /** Open the builder; the first time (or after "?"), with the coach marks. */
  const openBuilder = (v: Omit<BuilderView, "kind"> = {}) => {
    // A brand-new quote needs room on the plan; a re-quote joins an existing one, so it's free.
    if (!v.prefill && !canAdd) {
      setView({ kind: "unlock" });
      return;
    }
    if (!seen && !v.prefill) {
      touring.current = true;
      setCoachStep(COACH_STEPS[0]);
    }
    setView({ kind: "builder", ...v });
  };

  // The list: the model's opportunities on the server (any device) + this browser's registry
  // (legacy pre-model quotes, and ones just placed that the list hasn't caught up with), minus
  // what Remove hid here.
  const registry = source.useRegistry();
  const hidden = source.useHidden();
  const server = source.useServerQuotes();
  const quotes = useMemo(() => mergeQuotes(registry, server.items, hidden), [registry, server.items, hidden]);
  // Local-only entries are read by id. One filed under the model waits for the model's list first
  // (it's usually in it); a legacy one (no model) never will be.
  const localIds = useMemo(
    () => quotes.filter((q) => !q.opp && (server.settled || !q.entry.modelId)).map((q) => q.entry.opportunityId),
    [quotes, server.settled],
  );
  const byId = source.useOpportunities(localIds);
  const quoteOf = useMemo(() => new Map(quotes.map((q) => [q.entry.opportunityId, q])), [quotes]);
  const serverRefetch = server.refetch;
  const oppOf = useCallback(
    (id: string): OppState => {
      const q = quoteOf.get(id);
      if (q?.opp) return { data: q.opp as Opportunity, isLoading: false, isError: false, notFound: false, error: null, refetch: serverRefetch };
      if (q && q.entry.modelId && !server.settled) return { data: undefined, isLoading: true, isError: false, notFound: false, error: null, refetch: serverRefetch };
      return byId(id);
    },
    [quoteOf, byId, server.settled, serverRefetch],
  );

  // ── the plan (in-app purchase): active quotes, Real and Paper alike, against the tier's limit ──
  const plan = source.usePlan();
  // Counted over the merged list (every device's quotes + the local legacy ones).
  const active = useMemo(
    () =>
      quotes.filter(({ entry: e }) => {
        const st = oppOf(e.opportunityId);
        if (st.notFound) return false;
        if (!st.data) return true; // still loading: count it rather than let one slip past the cap
        return classifyStatus(buildOppBook(e.sides, (st.data.legs ?? []) as MmLeg[], e.legs)) !== "closed";
      }).length,
    [quotes, oppOf],
  );
  // Don't upsell before holdings have loaded: a Pro user would briefly look Free.
  const canAdd = plan.loading || canAddQuote(plan.tier, active);

  // A link's "quote this market": once the plan and the remembered quotes have loaded (so the
  // active count is honest), through the same gate as "+ New quote".
  const linkDone = useRef<string | null>(null);
  const oppsLoading = !server.settled || quotes.some((q) => oppOf(q.entry.opportunityId).isLoading);
  useEffect(() => {
    if (!openQuote || plan.loading || oppsLoading) return;
    const k = JSON.stringify(openQuote);
    if (linkDone.current === k) return;
    linkDone.current = k;
    openBuilder({ contestId: openQuote.contestId, start: openQuote.start });
    onLinkUsed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openQuote, plan.loading, oppsLoading]);

  const items = useMemo(() => {
    const rows = quotes
      .filter((q) => q.entry.ccy === ccy)
      .map((q) => {
        const st = oppOf(q.entry.opportunityId);
        // The model as the server has it (null on a quote from before models): every later order
        // on the opportunity sends exactly this, or none.
        const entry: MmEntry = { ...q.entry, modelId: st.data ? (st.data.model_id ?? null) : (q.entry.modelId ?? null) };
        const legs = (st.data?.legs ?? []) as MmLeg[];
        const book = buildOppBook(entry.sides, legs, entry.legs);
        const status: MmStatus = st.data ? classifyStatus(book) : "working";
        return {
          id: entry.opportunityId,
          entry,
          st,
          book,
          status,
          createdAt: entry.createdAt,
        };
      })
      // Another account's ids in this browser (or ones that are gone) answer 404: never shown.
      .filter((r) => !r.st.notFound);
    return sortOpportunities(rows);
  }, [quotes, ccy, oppOf]);

  // Dev preview: open a re-quote once its opportunity has loaded.
  const requoteOpened = useRef(false);
  useEffect(() => {
    if (initialView?.kind !== "requote" || requoteOpened.current) return;
    const it = items.find((i) => i.id === initialView.opportunityId && i.st.data);
    if (!it) return;
    requoteOpened.current = true;
    setView({
      kind: "builder",
      prefill: { entry: it.entry, ...requoteFrom(it.entry, it.book) },
    });
  }, [initialView, items]);

  const placed = useCallback(
    (id: string, legs: PlacedLeg[], placedCcy: Ccy) => {
      setLastPlaced({ id, legs });
      setFocusId(id);
      setCcy(placedCcy);
      setCoachStep(null);
      if (touring.current) {
        touring.current = false;
        setPostTip(true);
        setExplainer(false);
        markSeen();
      }
      setView({ kind: "list" });
    },
    [markSeen],
  );

  // Bring the card just placed into view.
  const focusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (focusId)
      requestAnimationFrame(() =>
        focusRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        }),
      );
  }, [focusId]);

  useEffect(() => {
    if (!linkScroll.current || !focusId) return;
    const entry = quoteOf.get(focusId)?.entry;
    if (!entry) return;
    if (entry.ccy !== ccy) {
      setCcy(entry.ccy);
      return;
    }
    if (!focusRef.current) return;
    linkScroll.current = false;
    focusRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [items, quoteOf, focusId, ccy]);

  const isMobile = useIsMobile();
  const exposed = items.filter((i) => i.status === "exposed").length;
  const loading = items.some((i) => i.st.isLoading);

  const closeBuilder = () => {
    setCoachStep(null);
    setView({ kind: "list" });
  };
  // The builder never replaces the list: it's pinned to the right on desktop and a bottom sheet
  // on mobile, so the quotes being monitored stay in view while a new one is set up.
  const builder =
    view.kind === "builder" ? (
      <QuoteBuilder
        // A new prefill (Re-quote on another card) starts a fresh builder.
        key={view.prefill?.entry.opportunityId ?? "new"}
        source={source}
        prefill={view.prefill}
        contestId={view.contestId}
        paper={view.paper}
        initialMargin={view.margin}
        start={view.start}
        coach={coachStep ? coach : undefined}
        onPlaced={placed}
        onClose={closeBuilder}
      />
    ) : view.kind === "unlock" ? (
      <UnlockQuotes plan={plan} active={active} onClose={closeBuilder} onUnlocked={() => setView({ kind: "builder" })} />
    ) : null;

  return (
    <div className="flex h-full min-h-0">
      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 p-4">
          <>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="mr-1 text-[15px] font-semibold text-ink">Market making</h1>
              <div className="flex items-center gap-2" role="group" aria-label="Book">
                <Pill active={ccy === "USD"} onClick={() => setCcy("USD")}>
                  Real · USD
                </Pill>
                <Pill active={ccy === "ATLAS"} onClick={() => setCcy("ATLAS")}>
                  Paper · ATLAS
                </Pill>
              </div>
              <button
                type="button"
                onClick={replay}
                aria-label="How market making works"
                title="How market making works"
                className={`${btnBase} ${btnIdle} ml-auto w-11 !px-0 lg:w-8`}
              >
                ?
              </button>
              {!plan.loading && plan.tier !== "unlimited" && (
                <span className="nums text-[11px] text-faint" title="Active quotes on your plan">
                  {TIER_NAME[plan.tier]} · {active}/{TIER_LIMIT[plan.tier]}
                </span>
              )}
              <button type="button" onClick={() => openBuilder()} className={`${btnBase} ${btnPrimary} font-semibold`}>
                {canAdd ? "+ New quote" : "Unlock more quotes"}
              </button>
            </div>
            {ccyOf(mode) !== ccy && items.length > 0 && (
              <p className="text-[11px] text-faint">
                Viewing the {ccy === "USD" ? "Real" : "Paper"} book. New quotes, re-quotes and hedges follow your trading mode ({wantsReal ? "Real" : "Paper"}).
              </p>
            )}

            {server.isError && (
              <p className="text-[12px] text-amber">
                Couldn't load your quotes from OpenMarkets, so only the ones this browser remembers show.{" "}
                <button type="button" onClick={server.refetch} className="underline hover:text-ink">
                  Retry
                </button>
              </p>
            )}

            {items.length === 0 && !explainer && !server.settled ? (
              <Skeleton className="h-28 w-full" />
            ) : (items.length === 0 || explainer) && (
              <Explainer
                source={source}
                ccy={ccy}
                hasQuotes={items.length > 0}
                onTryPaper={(contestId) => openBuilder({ contestId, paper: true })}
                onNew={() => openBuilder()}
                onDismiss={
                  items.length > 0
                    ? () => {
                        setExplainer(false);
                        markSeen();
                      }
                    : undefined
                }
              />
            )}

            {items.length > 0 && (
              <>
                <SectionHeading
                  title="Opportunities"
                  count={items.length}
                  className="!pt-1"
                  right={
                    exposed > 0 ? (
                      <span className="rounded-full bg-down/15 px-2 py-0.5 text-[10px] font-semibold text-down">{exposed} exposed</span>
                    ) : loading ? (
                      <Skeleton className="h-3 w-16" />
                    ) : undefined
                  }
                />
                <div className="flex flex-col gap-2">
                  {items.map((it) => (
                    <div key={it.id} ref={it.id === focusId ? focusRef : undefined} className="scroll-mt-4">
                      <QuoteCard
                        source={source}
                        entry={it.entry}
                        st={it.st}
                        book={it.book}
                        ccy={ccy}
                        mode={mode}
                        focused={it.id === focusId}
                        placed={lastPlaced?.id === it.id ? lastPlaced.legs : null}
                        tip={postTip && it.id === focusId}
                        onTipDone={() => setPostTip(false)}
                        onDismissPlaced={() => setLastPlaced(null)}
                        onPlaced={(legs) => {
                          setLastPlaced({ id: it.id, legs });
                          setFocusId(it.id);
                        }}
                        onRequote={(prefill) => setView({ kind: "builder", prefill })}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        </div>
      </div>
      {builder &&
        (isMobile ? (
          <BottomSheet open onClose={closeBuilder} label={view.kind === "unlock" ? "More quotes" : view.kind === "builder" && view.prefill ? "Re-quote" : "New quote"}>
            <div className="p-3">{builder}</div>
          </BottomSheet>
        ) : (
          <aside className="gb-drawer-in w-[440px] shrink-0 overflow-y-auto border-l border-border bg-panel p-3" aria-label="New quote">
            {builder}
          </aside>
        ))}
    </div>
  );
}

/* ===================================================================== card */

/*
 * The tracking card (Revision 2, "Card layout (compact)"):
 *   row 1  game · market (one line) and the state chip
 *   row 2  the big live number (MmKpi) with its caption and secondary lines, and the primary
 *          action: [Get out for −$X] when exposed, [Move bid] when the market left a working bid
 *   row 3  one fill line per side, in contracts, with a thin bar
 *   Details  the venue legs, Cancel resting, Re-quote, a custom-amount get-out, and fees
 */
interface OppCardProps {
  source: MarketMakingSource;
  entry: MmEntry;
  st: OppState;
  book: OppBook;
  ccy: Ccy;
  mode: MoneyMode;
  focused: boolean;
  placed: PlacedLeg[] | null;
  /** Show the walkthrough's post-place tip here. */
  tip: boolean;
  onTipDone: () => void;
  onDismissPlaced: () => void;
  onPlaced: (legs: PlacedLeg[]) => void;
  onRequote: (p: QuotePrefill) => void;
}

/** A card: straight from the local entry, or rebuilt first when the quote came from another device. */
function QuoteCard(props: OppCardProps) {
  return props.entry.remote ? <RemoteOppCard {...props} /> : <OppCard {...props} />;
}

/**
 * A quote placed on another device (no local entry): its game label from the contest, and its
 * market, line and sides by matching the legs' position hashes against the game's quotable books.
 * Until those load (or if the line has left the feed) the sides are the legs' own positions.
 */
function RemoteOppCard(props: OppCardProps) {
  const { source, entry: provisional, st } = props;
  const contest = source.useContest(provisional.contestId || undefined);
  const gb = source.useGameBook(provisional.contestId || undefined);
  const markets = useMemo(() => (gb.positions && gb.markets ? quotableMarkets(gb.positions, gb.markets) : null), [gb.positions, gb.markets]);
  const game = contest ? gameLabel(contest, "") : "";
  const rebuilt = st.data ? { ...entryFromOpportunity(st.data, { game, markets }), modelId: provisional.modelId } : provisional;
  // Prices stream: keep the same entry while what it says hasn't changed.
  const sig = JSON.stringify([rebuilt.game, rebuilt.marketKey, rebuilt.bookKey, rebuilt.market, rebuilt.sides, rebuilt.legs, rebuilt.modelId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const entry = useMemo(() => rebuilt, [sig]);
  const legs = st.data?.legs;
  const book = useMemo(() => buildOppBook(entry.sides, (legs ?? []) as MmLeg[], entry.legs), [entry, legs]);
  return <OppCard {...props} entry={entry} book={book} />;
}

function OppCard({
  source,
  entry,
  st,
  book,
  ccy,
  mode,
  focused,
  placed,
  tip,
  onTipDone,
  onDismissPlaced,
  onPlaced,
  onRequote,
}: OppCardProps) {
  const hashes = useMemo(() => entry.sides.map((s) => s.positionHash), [entry.sides]);
  const prices = source.useSidePrices(entry.contestId, hashes);
  const real = entry.ccy === "USD";
  const scope = source.useScope(modeOf(entry.ccy));
  const orders = source.useOrders();
  const [details, setDetails] = useState(false);
  const [custom, setCustom] = useState(false);
  const [panel, setPanel] = useState<{ kind: "getout" } | { kind: "move"; col: 0 | 1 } | null>(null);
  const [cancel, setCancel] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });

  // Each side's live position: the exact one, else the other side's complement (a re-listed line).
  const live = useMemo((): [LiquidityPosition | undefined, LiquidityPosition | undefined] => {
    const exact = (i: 0 | 1) => prices?.find((p) => p.position_hash === hashes[i]);
    const comp = (i: 0 | 1) =>
      prices ? (findComplement(prices, hashes[i === 0 ? 1 : 0], scope.allowed)?.position as LiquidityPosition | undefined) : undefined;
    return [exact(0) ?? comp(0), exact(1) ?? comp(1)];
  }, [prices, hashes, scope.allowed]);

  const state = kpiState(book);
  const need = hedgeNeed(book);
  // Live numbers come from venue prices: the stream patches those, not consensus_price.
  const out = useMemo(() => getOut(book, live, scope.allowed, { ccy: entry.ccy, fees: real }), [book, live, scope.allowed, entry.ccy, real]);
  const quotes = useMemo(() => [liveQuotes(live[0], scope.allowed), liveQuotes(live[1], scope.allowed)] as const, [live, scope.allowed]);
  const vs = useMemo(() => bidsVsMarket(book, quotes), [book, quotes]);
  const moves = useMemo(
    () => (state === "working" ? movedAway(book, live, scope.allowed, entry.legs, { fees: real }) : []),
    [state, book, live, scope.allowed, entry.legs, real],
  );
  const longResting = useMemo(() => (need ? longRestingIds(book, need.long) : []), [book, need]);

  const value =
    state === "working"
      ? Math.min(...ifAllFill(book))
      : state === "exposed"
        ? (out?.locked ?? null)
        : state === "locked"
          ? Math.min(...book.payout)
          : settledResult(book);
  const held = book.sides[0].filled + book.sides[1].filled;

  const cancelResting = async () => {
    const ids = book.resting.map((l) => l.router_order_id);
    if (ids.length === 0) return;
    setCancel({ busy: true, error: null });
    try {
      const res = await orders.cancel(ids);
      setCancel({
        busy: false,
        error: res.errors?.length ? res.errors.map((e) => e.error).join(" · ") : null,
      });
      st.refetch();
    } catch (e) {
      setCancel({ busy: false, error: errorText(e) });
    }
  };
  const done = (legs: PlacedLeg[]) => {
    setPanel(null);
    setCustom(false);
    onPlaced(legs);
    st.refetch();
  };
  const canAct = orders.client === "ok";
  const label = (i: 0 | 1) => entry.sides[i].label;

  /* ── row 2: one number, a few words, one action ── */
  const caption =
    state === "working"
      ? "if both fill"
      : state === "exposed"
        ? out
          ? "to get out now"
          : prices
            ? "no price to get out"
            : "loading prices…"
        : state === "locked"
          ? "locked"
          : held <= 0
            ? "nothing filled"
            : "final";
  const kpi = <MmKpi state={state} value={value} ccy={ccy} caption={caption} />;
  let action: ReactNode = null;
  if (state === "working" && moves[0]) {
    action = (
      <button
        type="button"
        disabled={!canAct}
        aria-expanded={panel?.kind === "move"}
        onClick={() => setPanel(panel?.kind === "move" ? null : { kind: "move", col: moves[0].col })}
        className={`${btnBase} ${btnPrimary} w-full font-semibold sm:w-auto`}
        title={`The market moved away from your ${label(moves[0].col)} bid`}
      >
        Move bid
      </button>
    );
  } else if (state === "exposed" && need) {
    action = (
      <button
        type="button"
        disabled={!out || !canAct}
        aria-expanded={panel?.kind === "getout"}
        onClick={() => setPanel(panel?.kind === "getout" ? null : { kind: "getout" })}
        className={`${btnBase} w-full font-semibold sm:w-auto ${
          out && out.locked < -0.005 ? "border-down/60 bg-down/15 text-down enabled:hover:bg-down/25" : "border-up/60 bg-up/15 text-up enabled:hover:bg-up/25"
        }`}
      >
        Get out
      </button>
    );
  }
  // Per side: your resting bid vs the cheapest live price (red once it's run 3¢+ past you).
  const bidOf = (col: 0 | 1) => vs.find((v) => v.col === col) ?? null;

  return (
    <SoftCard highlight={focused} className="p-3">
      {/* row 1 */}
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1 truncate text-[14px] font-medium text-ink" title={`${entry.game} · ${entry.market}`}>
          {entry.game ? shortGame(entry.game) : "…"} <span className="text-dim">· {shortMarket(entry.market)}</span>
        </div>
        {st.isLoading && !st.data ? (
          <Skeleton className="h-5 w-20" />
        ) : (
          <KpiChip state={state} value={value}>
            {KPI_LABEL[state]}
          </KpiChip>
        )}
      </div>

      {st.isError && !st.data ? (
        <div className="mt-2">
          <ErrorState error={st.error} onRetry={st.refetch} />
        </div>
      ) : !st.data ? (
        <Skeleton className="mt-2 h-20 w-full" />
      ) : (
        <>
          {tip && <PostPlaceTip onDone={onTipDone} />}
          {placed && (
            <div className="mt-2">
              <PlacedResult
                legs={placed}
                ccy={ccy}
                onDismiss={onDismissPlaced}
                note={mode === "paper" ? "Paper orders rest on the practice book (OpenMarkets)." : undefined}
              />
            </div>
          )}

          {/* row 2 */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1 basis-[16rem]">{kpi}</div>
            {action && <div className="w-full sm:w-auto">{action}</div>}
          </div>
          {/* Open until closed or placed, whatever the card turns into meanwhile: the confirm owns an
              order sheet, and a cancel-first that flips the card mustn't unmount it mid-send. */}
          {panel?.kind === "getout" && (
            <GetOutConfirm source={source} entry={entry} book={book} live={out} longResting={longResting} onPlaced={done} onClose={() => setPanel(null)} />
          )}
          {panel?.kind === "move" && (
            <MoveBidConfirm
              key={panel.col}
              source={source}
              entry={entry}
              live={moves.find((m) => m.col === panel.col) ?? null}
              onPlaced={done}
              onClose={() => setPanel(null)}
            />
          )}

          {/* row 3 */}
          <div className="mt-3 flex flex-col gap-2">
            {book.sides.map((s, i) => (
              <SideRow key={s.side.positionHash} side={s} bid={bidOf(i as 0 | 1)} ifWins={held > 0 ? book.payout[i] : null} ccy={ccy} />
            ))}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-expanded={details}
              onClick={() => setDetails((v) => !v)}
              className="flex min-h-11 items-center gap-1.5 text-[12px] text-dim hover:text-ink lg:min-h-8"
            >
              <span className={`text-[10px] transition-transform duration-150 motion-reduce:transition-none ${details ? "rotate-90" : ""}`}>▸</span>
              Details
            </button>
            {state === "settled" && (
              // Forgets the local entry and hides it in this browser: a server-listed quote stays
              // on the account and in the model's list (see liveSource.forget).
              <button
                type="button"
                onClick={() => source.forget(entry.opportunityId)}
                className="ml-auto min-h-11 px-1 text-[12px] text-faint hover:text-ink lg:min-h-8"
                title="Remove it from this list (it stays on your account)"
              >
                Remove
              </button>
            )}
          </div>

          {details && (
            <div className="gb-fade flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {book.resting.length > 0 && (
                  <button type="button" disabled={cancel.busy || !canAct} onClick={() => void cancelResting()} className={`${btnBase} ${btnDanger}`}>
                    {cancel.busy ? "Cancelling…" : `Cancel resting (${book.resting.length})`}
                  </button>
                )}
                {state !== "settled" && (
                  <button
                    type="button"
                    disabled={!canAct}
                    onClick={() => onRequote({ entry, ...requoteFrom(entry, book) })}
                    className={`${btnBase} ${btnIdle}`}
                  >
                    Re-quote
                  </button>
                )}
                {need && (
                  <button type="button" aria-expanded={custom} onClick={() => setCustom((v) => !v)} className={`${btnBase} ${custom ? btnOn : btnIdle}`}>
                    Get out: custom amount
                  </button>
                )}
                <Link to={marketHref({ contestId: entry.contestId, marketKey: entry.marketKey, bookKey: entry.bookKey })} className={`${btnBase} ${btnIdle} hover:border-amber/40 hover:text-ink`}>
                  View market
                </Link>
                <span className="nums ml-auto text-[11px] text-faint">
                  {!entry.modelId && (
                    <span title="Not filed under greenbook's OpenMarkets model (placed before quotes were, or the model couldn't be created), so only this browser lists it">
                      This browser only ·{" "}
                    </span>
                  )}
                  {entry.remote && <span title="Placed from greenbook on another device">Another device · </span>}
                  {book.fees > 0 ? `Fees so far ≈ ${money(book.fees, ccy)} · ` : ""}
                  Quoted {fmtDateTime(new Date(entry.createdAt).toISOString())}
                </span>
              </div>
              {cancel.error && <p className="text-[12px] text-down">{cancel.error}</p>}
              {book.unmatched.length > 0 && (
                <p className="text-[12px] text-amber">
                  {book.unmatched.length} leg
                  {book.unmatched.length === 1 ? "" : "s"} couldn't be placed on a side; {book.unmatched.length === 1 ? "its" : "their"} cost is included.
                </p>
              )}
              {custom && need && (
                <HedgePanel
                  source={source}
                  entry={entry}
                  book={book}
                  need={need}
                  shortPosition={live[need.short]}
                  ccy={entry.ccy}
                  mode={mode}
                  onClose={() => setCustom(false)}
                  onPlaced={done}
                />
              )}
              <LegTable legs={(st.data.legs ?? []) as MmLeg[]} entry={entry} book={book} ccy={ccy} />
            </div>
          )}
        </>
      )}
    </SoftCard>
  );
}

/**
 * Row 3, one line per side: name · fill bar · filled/quoted contracts · your price vs the market ·
 * what you make if this side wins. Numbers only; the market price turns red once it has run past
 * your resting bid (MOVED_AWAY_CENTS).
 */
function SideRow({ side, bid, ifWins, ccy }: { side: SideBook; bid: BidVsMarket | null; ifWins: number | null; ccy: Ccy }) {
  const { format } = useOddsFormat();
  const pct = side.quoted > 0 ? Math.min(100, (side.filled / side.quoted) * 100) : 0;
  const away = bid?.market ? bid.market.price * 100 - bid.bidCents >= MOVED_AWAY_CENTS : false;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 text-[12px] sm:grid-cols-[10rem_minmax(4rem,1fr)_4.5rem_9rem_4.5rem]">
      <span className="min-w-0 truncate text-[13px] text-ink" title={side.side.label}>
        {shortSide(side.side.label)}
      </span>
      <div
        className="order-last col-span-2 h-1.5 overflow-hidden rounded-full bg-border sm:order-none sm:col-span-1"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-label={`${side.side.label} filled`}
      >
        <div className="gb-bar h-full rounded-full bg-up" style={{ width: `${pct}%` }} />
      </div>
      <span className="nums hidden text-right text-dim sm:inline">
        <FlashText value={side.filled}>
          <span className="font-semibold text-ink">{fmtContracts(side.filled)}</span>
        </FlashText>
        /{fmtContracts(side.quoted)}
      </span>
      <span className="nums hidden text-dim sm:inline">
        {bid ? (
          <>
            bid <span className="text-ink">{fmtBid(bid.bidCents, format)}</span>
            {bid.market && (
              <>
                {" "}
                mkt{" "}
                <FlashText value={bid.market.price}>
                  <span className={away ? "text-down" : "text-ink"} title={bid.market.partnerName}>
                    {fmtPrice(bid.market.price, bid.market.american, format)}
                  </span>
                </FlashText>
              </>
            )}
          </>
        ) : side.avgPrice != null ? (
          <>
            avg <span className="text-ink">{fmtPrice(side.avgPrice, null, format)}</span>
          </>
        ) : null}
      </span>
      <span className={`nums text-right text-[13px] font-medium ${ifWins == null ? "text-faint" : toneOf(ifWins)}`} title="If this side wins">
        {ifWins == null ? "—" : signedMoney(ifWins, ccy)}
      </span>
    </div>
  );
}

/** Every leg, oldest first (`leg_number` restarts per request, so it isn't shown). */
function LegTable({ legs, entry, book, ccy }: { legs: MmLeg[]; entry: MmEntry; book: OppBook; ccy: Ccy }) {
  const { format } = useOddsFormat();
  const sideOf = (l: MmLeg) => (book.sides[0].legs.includes(l) ? entry.sides[0].label : book.sides[1].legs.includes(l) ? entry.sides[1].label : "?");
  return (
    <div className="overflow-hidden rounded-md border border-border/60 bg-bg">
      <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2 text-[11px] text-faint">
        <span className="uppercase tracking-wide">Venue legs</span>
        <span className="nums">{legs.length}</span>
        <span className="ml-auto truncate" title="opportunity_id">
          {entry.opportunityId}
        </span>
      </div>
      {legs.length === 0 ? (
        <div className="px-3 py-3 text-[12px] text-dim">No legs yet.</div>
      ) : (
        <ul className="flex flex-col divide-y divide-border/60">
          {sortLegs(legs).map((l) => {
            const ref = entry.legs[l.router_order_id];
            const target = ref?.partnerName;
            // Paper legs book at the practice book whatever venue they targeted.
            const booked = legVenueName(l);
            const state = isResting(l) ? "resting" : (l.result ?? l.fill_status ?? l.status ?? "—");
            const price = l.price ?? 0;
            return (
              <li key={l.router_order_id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 text-[12px]">
                <span className="nums w-28 shrink-0 text-faint">{fmtDateTime(l.created_at ?? l.placed_datetime)}</span>
                <span className="min-w-0 flex-1 text-ink">
                  {sideOf(l)} <span className="text-dim">· {l.order_type ?? "—"}</span>
                </span>
                <span className="text-dim" title={target && target !== booked ? `Targeted ${target}; booked at ${booked}` : undefined}>
                  {target && target !== booked ? `${target} → ${booked}` : booked}
                </span>
                <span className="nums text-ink">{fmtPrice(l.price, null, format)}</span>
                <span className="nums text-dim" title={`${money(l.filled_amt ?? 0, ccy)} of ${money(l.amount ?? 0, ccy)}`}>
                  <FlashText value={l.filled_amt ?? 0}>
                    <span className="text-ink">{fmtContracts(price > 0 ? (l.filled_amt ?? 0) / price : 0)}</span>
                  </FlashText>{" "}
                  of {fmtContracts(price > 0 ? (l.amount ?? 0) / price : 0)} ct · {money(l.amount ?? 0, ccy)}
                </span>
                <span className="text-dim">{String(state).replace(/_/g, " ")}</span>
                <div className="h-1 basis-full overflow-hidden rounded-full bg-border" aria-hidden>
                  <div className="gb-bar h-full rounded-full bg-up" style={{ width: `${fillPct(l)}%` }} />
                </div>
                {l.failure_reason && <span className="basis-full text-down">{l.failure_reason}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
